import React, { useEffect, useState } from 'react';
import { Icon } from 'assets/icons';
import { useLeadContactService } from 'hooks/api/agents/lead/contact';
import { useLeadStatusService } from 'hooks/api/agents/lead/status';
import { useLeadOrganisationService } from 'hooks/api/agents/lead/orgnisation';
import { useAdminService } from 'hooks/api/users/admin';
import { MultiSelect } from 'app/shared/select';
import { SimpleInput } from 'app/shared/input';
import { TextArea } from 'app/shared/input/textarea';
import ContextMenu from 'app/shared/datatable/context-menu/context-menu';
import Detail from './components/Detail';
import TagList from './components/TagList';
import ContactCard from './components/ContactCard';
import { SubjectTimeline } from 'app/member/modules/customers/shared/timeline/timeline';
import { ResourceLabels } from 'app/member/modules/customers/shared/labels/labels';
import styles from './view.module.css';

const buildQuickActions = (lead) =>
  [
    lead.linkedin_url && {
      key: 'linkedin',
      label: 'LinkedIn',
      icon: 'in',
      href: lead.linkedin_url,
      external: true
    },
    lead.phone && {
      key: 'call',
      label: 'Call',
      icon: '☏',
      href: `tel:${lead.phone}`,
      external: false
    }
  ].filter(Boolean);

const productFootprintLabel = (lead) =>
  lead.has_web_app == null && lead.has_mobile_app == null
    ? null
    : `${lead.has_web_app ? 'Web' : ''}${lead.has_web_app && lead.has_mobile_app ? ' + ' : ''}${
        lead.has_mobile_app ? 'Mobile' : ''
      }`;

const emailToolingLabel = (lead) =>
  lead.email_tool_detected == null ? null : lead.email_tool_detected ? 'Detected' : 'Not detected';

const basicInfoFromLead = (lead) => ({
  name: lead?.name ?? '',
  industry: lead?.industry ?? '',
  location: lead?.location || lead?.city || '',
  website: lead?.website ?? '',
  linkedin_url: lead?.linkedin_url ?? '',
  phone: lead?.phone ?? '',
  short_description: lead?.short_description ?? ''
});

const EMPTY_CONTACT = {
  first_name: '',
  last_name: '',
  title: '',
  email: '',
  phone: '',
  linkedin_url: ''
};

const SENTIMENT_OPTIONS = [
  { label: 'Positive', value: 'positive' },
  { label: 'Neutral', value: 'neutral' },
  { label: 'Negative', value: 'negative' }
];

const toCommaText = (values = []) => (Array.isArray(values) ? values.join(', ') : values || '');
const fromCommaText = (text = '') =>
  (text || '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);

const discoveryFromLead = (lead) => ({
  pain_points: toCommaText(lead?.pain_points),
  needs: toCommaText(lead?.needs),
  current_tools: toCommaText(lead?.current_tools),
  objections: toCommaText(lead?.objections),
  list_size: lead?.list_size ?? '',
  budget: lead?.budget ?? '',
  sentiment: lead?.sentiment ?? null,
  commencement_time: lead?.commencement_time ?? '',
  loss_reason: lead?.loss_reason ?? ''
});

const OrgScore = ({ score }) => {
  if (score == null) return null;
  const cls =
    score >= 70 ? styles.orgScoreHigh : score >= 40 ? styles.orgScoreMid : styles.orgScoreLow;
  return <span className={`${styles.orgScore} ${cls}`}>★ {score}</span>;
};

export const LeadOrgView = ({ lead, closeModal, onStageChange }) => {
  const { fetchLeads, createLead, updateLead } = useLeadContactService();
  const { updateLeadOrganisation, updateLeadStage, updateLeadKeyContact } =
    useLeadOrganisationService();
  const { fetchLeadStatuses } = useLeadStatusService();
  const { fetchAdmins } = useAdminService();

  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [statusesLoading, setStatusesLoading] = useState(false);
  const [currentSlug, setCurrentSlug] = useState(lead?.status_slug);
  const [admins, setAdmins] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [currentOwner, setCurrentOwner] = useState(lead?.owner ?? null);
  const [keyContactId, setKeyContactId] = useState(lead?.key_contact_id ?? null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState(EMPTY_CONTACT);
  const [editingContactId, setEditingContactId] = useState(null);
  const [savingContact, setSavingContact] = useState(false);
  const [discovery, setDiscovery] = useState(discoveryFromLead(lead));
  const [discoveryEditing, setDiscoveryEditing] = useState(false);
  const [savingDiscovery, setSavingDiscovery] = useState(false);
  const [basicInfo, setBasicInfo] = useState(basicInfoFromLead(lead));
  const [basicInfoEditing, setBasicInfoEditing] = useState(false);
  const [savingBasicInfo, setSavingBasicInfo] = useState(false);
  const [composerMode, setComposerMode] = useState(null);

  useEffect(() => {
    if (!lead) return;
    const id = lead.id || lead._id;
    if (!id) return;
    setCurrentSlug(lead.status_slug);
    setCurrentOwner(lead.owner ?? null);
    setKeyContactId(lead.key_contact_id ?? null);
    setDiscovery(discoveryFromLead(lead));
    setDiscoveryEditing(false);
    setBasicInfo(basicInfoFromLead(lead));
    setBasicInfoEditing(false);
    setContactsLoading(true);
    const contacts_query = lead.apollo_org_id ? `org_id=${lead.apollo_org_id}` : `lead_id=${id}`;
    fetchLeads({ query_string: contacts_query })
      .then(({ leads }) => {
        const list = leads || [];
        setContacts(list);
        const key_id = lead.key_contact_id ?? null;
        if (key_id != null && !list.some((c) => String(c.id || c._id) === String(key_id))) {
          setKeyContactId(null);
        }
      })
      .finally(() => setContactsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id, lead?._id]);

  useEffect(() => {
    setStatusesLoading(true);
    fetchLeadStatuses({ query_string: 'population=100' })
      .then(({ lead_statuses }) =>
        setStatuses(
          (lead_statuses || []).map((s) => ({ key: s.slug?.toLowerCase(), label: s.label }))
        )
      )
      .finally(() => setStatusesLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setAdminsLoading(true);
    fetchAdmins({ query_string: 'population=500' })
      .then(({ admins: fetched }) =>
        setAdmins(
          (fetched || []).map((admin) => {
            const id = String(admin.id || admin._id);
            const firstname = admin.firstname || admin.first_name || '';
            const lastname = admin.lastname || admin.last_name || '';
            return {
              value: id,
              label:
                admin.name || [firstname, lastname].filter(Boolean).join(' ') || admin.email || id,
              id,
              firstname,
              lastname,
              email: admin.email || ''
            };
          })
        )
      )
      .finally(() => setAdminsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStatusChange = async (selected) => {
    if (!selected || selected.value === currentSlug) return;
    const id = lead.id || lead._id;
    const ok = await updateLeadStage(id, selected.value);
    if (!ok) return;
    setCurrentSlug(selected.value);
    onStageChange?.(id, selected.value);
  };

  const handleOwnerChange = async (selected) => {
    const owner = selected
      ? {
          id: selected.id,
          firstname: selected.firstname,
          lastname: selected.lastname,
          email: selected.email
        }
      : null;
    const id = lead.id || lead._id;
    await updateLeadOrganisation(id, { data: { owner } });
    setCurrentOwner(owner);
  };

  const handleMakeKeyContact = async (contact) => {
    const contact_id = contact.id || contact._id;
    if (!contact_id) return;
    const id = lead.id || lead._id;
    const previous = keyContactId;
    setKeyContactId(contact_id);
    const ok = await updateLeadKeyContact(id, contact_id);
    if (!ok) setKeyContactId(previous);
  };

  const setDiscoveryField = (field, value) => setDiscovery((prev) => ({ ...prev, [field]: value }));

  const handleCancelDiscovery = () => {
    setDiscovery(discoveryFromLead(lead));
    setDiscoveryEditing(false);
  };

  const handleSaveDiscovery = async () => {
    const id = lead.id || lead._id;
    setSavingDiscovery(true);
    const data = {
      pain_points: fromCommaText(discovery.pain_points),
      needs: fromCommaText(discovery.needs),
      current_tools: fromCommaText(discovery.current_tools),
      objections: fromCommaText(discovery.objections),
      list_size: discovery.list_size === '' ? null : Number(discovery.list_size),
      budget: discovery.budget === '' ? null : Number(discovery.budget),
      sentiment: discovery.sentiment || null,
      commencement_time: discovery.commencement_time || null,
      loss_reason: discovery.loss_reason.trim() || null
    };
    const ok = await updateLeadOrganisation(id, { data });
    setSavingDiscovery(false);
    if (ok !== false) setDiscoveryEditing(false);
  };

  const setBasicInfoField = (field, value) => setBasicInfo((prev) => ({ ...prev, [field]: value }));

  const handleCancelBasicInfo = () => {
    setBasicInfo(basicInfoFromLead(lead));
    setBasicInfoEditing(false);
  };

  const handleSaveBasicInfo = async () => {
    const name = basicInfo.name.trim();
    if (!name) return;

    const id = lead.id || lead._id;
    setSavingBasicInfo(true);
    const data = {
      name,
      industry: basicInfo.industry.trim() || null,
      location: basicInfo.location.trim() || null,
      website: basicInfo.website.trim() || null,
      linkedin_url: basicInfo.linkedin_url.trim() || null,
      phone: basicInfo.phone.trim() || null,
      short_description: basicInfo.short_description.trim() || null
    };
    const ok = await updateLeadOrganisation(id, { data });
    setSavingBasicInfo(false);
    if (ok) {
      setBasicInfo((prev) => ({ ...prev, ...data }));
      setBasicInfoEditing(false);
    }
  };

  const handleContactField = (field, value) =>
    setContactForm((prev) => ({ ...prev, [field]: value }));

  const handleAddContactClick = () => {
    setShowContactForm((show) => {
      const next = !show;
      if (next) {
        setEditingContactId(null);
        setContactForm(EMPTY_CONTACT);
      }
      return next;
    });
  };

  const handleEditContactClick = (contact) => {
    setEditingContactId(contact.id || contact._id);
    setContactForm({
      first_name: contact.first_name || '',
      last_name: contact.last_name || '',
      title: contact.title || '',
      email: contact.email || '',
      phone: contact.phone || '',
      linkedin_url: contact.linkedin_url || ''
    });
    setShowContactForm(true);
  };

  const handleCancelContactForm = () => {
    setShowContactForm(false);
    setEditingContactId(null);
    setContactForm(EMPTY_CONTACT);
  };

  const handleSaveContact = async () => {
    const first_name = contactForm.first_name.trim();
    if (!first_name) return;

    setSavingContact(true);

    if (editingContactId) {
      const data = {
        first_name,
        last_name: contactForm.last_name.trim(),
        title: contactForm.title.trim(),
        email: contactForm.email.trim() || null,
        phone: contactForm.phone.trim() || null,
        linkedin_url: contactForm.linkedin_url.trim() || null,
        has_email: Boolean(contactForm.email.trim()),
        has_phone_number: Boolean(contactForm.phone.trim())
      };

      const ok = await updateLead(editingContactId, { data });
      setSavingContact(false);

      if (ok) {
        setContacts((prev) =>
          prev.map((contact) =>
            String(contact.id || contact._id) === String(editingContactId)
              ? { ...contact, ...data }
              : contact
          )
        );
        handleCancelContactForm();
      }
      return;
    }

    const data = {
      lead_id: lead.id || lead._id,
      org_id: lead.apollo_org_id || null,
      source: 'manual',
      first_name,
      last_name: contactForm.last_name.trim(),
      title: contactForm.title.trim(),
      email: contactForm.email.trim() || null,
      phone: contactForm.phone.trim() || null,
      linkedin_url: contactForm.linkedin_url.trim() || null,
      has_email: Boolean(contactForm.email.trim()),
      has_phone_number: Boolean(contactForm.phone.trim())
    };

    const { lead: created } = await createLead(data);
    setSavingContact(false);

    if (created && (created.id || created._id)) {
      setContacts((prev) => [created, ...prev]);
      handleCancelContactForm();
    }
  };

  if (!lead) return null;

  const currentStatus = statuses.find((s) => s.key === currentSlug);
  const websiteHost = basicInfo.website
    ? basicInfo.website.replace(/^https?:\/\//, '').replace(/\/$/, '')
    : '';
  const quickActions = buildQuickActions(basicInfo);
  const contactsPreview = contacts.slice(0, 5);

  const sentimentLabel = SENTIMENT_OPTIONS.find((o) => o.value === discovery.sentiment)?.label;
  const discoveryDisplay = [
    { label: 'Pain points', value: discovery.pain_points },
    { label: 'Needs', value: discovery.needs },
    { label: 'Current tools', value: discovery.current_tools },
    { label: 'Objections', value: discovery.objections },
    { label: 'Sentiment', value: sentimentLabel },
    { label: 'List size', value: discovery.list_size === '' ? '' : discovery.list_size },
    { label: 'Budget', value: discovery.budget === '' ? '' : discovery.budget },
    { label: 'Commencement', value: discovery.commencement_time }
  ];

  const timelineSubject = { kind: 'lead', id: lead.id || lead._id, name: basicInfo.name };
  const timelineHost =
    currentOwner && typeof currentOwner === 'object'
      ? {
          id: currentOwner.id != null ? String(currentOwner.id) : '',
          firstname: currentOwner.firstname || '',
          lastname: currentOwner.lastname || '',
          email: currentOwner.email || ''
        }
      : null;
  const keyContact = contacts.find((c) => String(c.id || c._id) === String(keyContactId));
  const timelineInvitee = keyContact
    ? {
        id: keyContact.id || keyContact._id,
        firstname: keyContact.first_name || '',
        lastname: keyContact.last_name || '',
        email: keyContact.email || '',
        phone: keyContact.phone || ''
      }
    : null;

  return (
    <div className={styles.wrapper}>
      {/* Left pane */}
      <div className={styles.left}>
        <section className={styles.overviewCard}>
          <header className={styles.hero}>
            <div className={styles.heroIdentity}>
              <OrgScore score={lead.score} />
              <h1 className={styles.name}>{basicInfo.name || '—'}</h1>
            </div>
            <div className={styles.heroAside}>
              <div className={styles.statusSelector}>
                <MultiSelect
                  options={statuses.map((s) => ({ value: s.key, label: s.label }))}
                  value={
                    currentStatus
                      ? { value: currentStatus.key, label: currentStatus.label }
                      : currentSlug
                      ? { value: currentSlug, label: currentSlug }
                      : null
                  }
                  onChange={handleStatusChange}
                  isMulti={false}
                  isClearable={false}
                  placeholder="Set stage"
                  isLoading={statusesLoading}
                />
              </div>
              <div className={styles.statusSelector}>
                <MultiSelect
                  options={admins}
                  value={
                    admins.find(
                      (admin) => admin.value === String(currentOwner?.id ?? currentOwner ?? '')
                    ) || null
                  }
                  onChange={handleOwnerChange}
                  isMulti={false}
                  isClearable
                  placeholder="Assign owner"
                  isLoading={adminsLoading}
                  menuPlacement="auto"
                />
              </div>
            </div>
          </header>

          <ResourceLabels
            resource={{ kind: 'lead', id: lead.id || lead._id }}
            show_picker={composerMode === 'label'}
            onClosePicker={() => setComposerMode(null)}
          />

          {quickActions.length > 0 && (
            <div className={styles.quickActions}>
              {quickActions.map((a) => (
                <a
                  key={a.key}
                  href={a.href}
                  {...(a.external ? { target: '_blank', rel: 'noreferrer noopener' } : {})}
                  className={styles.quickAction}
                >
                  <span className={styles.quickActionIcon}>{a.icon}</span>
                  {a.label}
                </a>
              ))}
            </div>
          )}
        </section>

        <section className={`${styles.section} ${styles.aboutSection}`}>
          <div className={styles.contactsHeader}>
            <h2 className={styles.sectionTitle}>Basic Information</h2>
            <div className={styles.headerActions}>
              {basicInfoEditing ? (
                <>
                  <button
                    type="button"
                    className={styles.addContactBtn}
                    onClick={handleCancelBasicInfo}
                    disabled={savingBasicInfo}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={styles.saveBtn}
                    onClick={handleSaveBasicInfo}
                    disabled={savingBasicInfo || !basicInfo.name.trim()}
                  >
                    {savingBasicInfo ? 'Saving…' : 'Save'}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className={styles.addContactBtn}
                  onClick={() => setBasicInfoEditing(true)}
                >
                  Edit
                </button>
              )}
            </div>
          </div>

          {basicInfoEditing ? (
            <>
              <div className={styles.discoveryGrid}>
                <div className={styles.discoveryField}>
                  <label>Name</label>
                  <SimpleInput
                    className={styles.contactInput}
                    value={basicInfo.name}
                    onInput={(value) => setBasicInfoField('name', value)}
                  />
                </div>
                <div className={styles.discoveryField}>
                  <label>Industry</label>
                  <SimpleInput
                    className={styles.contactInput}
                    value={basicInfo.industry}
                    onInput={(value) => setBasicInfoField('industry', value)}
                  />
                </div>
                <div className={styles.discoveryField}>
                  <label>Location</label>
                  <SimpleInput
                    className={styles.contactInput}
                    value={basicInfo.location}
                    onInput={(value) => setBasicInfoField('location', value)}
                  />
                </div>
                <div className={styles.discoveryField}>
                  <label>Website</label>
                  <SimpleInput
                    className={styles.contactInput}
                    value={basicInfo.website}
                    onInput={(value) => setBasicInfoField('website', value)}
                  />
                </div>
                <div className={styles.discoveryField}>
                  <label>LinkedIn</label>
                  <SimpleInput
                    className={styles.contactInput}
                    value={basicInfo.linkedin_url}
                    onInput={(value) => setBasicInfoField('linkedin_url', value)}
                  />
                </div>
                <div className={styles.discoveryField}>
                  <label>Phone</label>
                  <SimpleInput
                    className={styles.contactInput}
                    value={basicInfo.phone}
                    onInput={(value) => setBasicInfoField('phone', value)}
                  />
                </div>
              </div>

              <div className={`${styles.discoveryField} ${styles.discoveryFull}`}>
                <label>About</label>
                <TextArea
                  rows={2}
                  placeholder="Short description…"
                  className={styles.discoveryTextarea}
                  value={basicInfo.short_description}
                  onInput={(value) => setBasicInfoField('short_description', value)}
                />
              </div>
            </>
          ) : (
            <div className={`${styles.grid} ${styles.snapshotGrid}`}>
              <Detail label="Industry" value={basicInfo.industry} />
              <Detail label="Location" value={basicInfo.location} />
              <Detail label="Website" value={websiteHost} />
              <Detail label="LinkedIn" value={basicInfo.linkedin_url} />
              <Detail label="Phone" value={basicInfo.phone} />
              <Detail label="Product footprint" value={productFootprintLabel(lead)} />
              <Detail label="Email tooling" value={emailToolingLabel(lead)} />
            </div>
          )}
        </section>

        <div className={styles.dossierRow}>
          <section
            className={`${styles.section} ${styles.aboutSection} ${styles.dossierCol} ${styles.dossierWide}`}
          >
            <div className={styles.contactsHeader}>
              <h2 className={styles.sectionTitle}>Discovery</h2>
              <div className={styles.headerActions}>
                {discoveryEditing ? (
                  <>
                    <button
                      type="button"
                      className={styles.addContactBtn}
                      onClick={handleCancelDiscovery}
                      disabled={savingDiscovery}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={styles.saveBtn}
                      onClick={handleSaveDiscovery}
                      disabled={savingDiscovery}
                    >
                      {savingDiscovery ? 'Saving…' : 'Save'}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className={styles.addContactBtn}
                    onClick={() => setDiscoveryEditing(true)}
                  >
                    Edit
                  </button>
                )}
              </div>
            </div>

            <div className={styles.cardScroll}>
              {discoveryEditing ? (
                <>
                  <div className={styles.discoveryGrid}>
                    <div className={styles.discoveryField}>
                      <label>Pain points</label>
                      <TextArea
                        rows={2}
                        placeholder="Comma separated…"
                        className={styles.discoveryTextarea}
                        value={discovery.pain_points}
                        onInput={(value) => setDiscoveryField('pain_points', value)}
                      />
                    </div>
                    <div className={styles.discoveryField}>
                      <label>Needs</label>
                      <TextArea
                        rows={2}
                        placeholder="Comma separated…"
                        className={styles.discoveryTextarea}
                        value={discovery.needs}
                        onInput={(value) => setDiscoveryField('needs', value)}
                      />
                    </div>
                    <div className={styles.discoveryField}>
                      <label>Current tools</label>
                      <TextArea
                        rows={2}
                        placeholder="Comma separated…"
                        className={styles.discoveryTextarea}
                        value={discovery.current_tools}
                        onInput={(value) => setDiscoveryField('current_tools', value)}
                      />
                    </div>
                    <div className={styles.discoveryField}>
                      <label>Objections</label>
                      <TextArea
                        rows={2}
                        placeholder="Comma separated…"
                        className={styles.discoveryTextarea}
                        value={discovery.objections}
                        onInput={(value) => setDiscoveryField('objections', value)}
                      />
                    </div>
                    <div className={styles.discoveryField}>
                      <label>Sentiment</label>
                      <MultiSelect
                        options={SENTIMENT_OPTIONS}
                        value={
                          SENTIMENT_OPTIONS.find((o) => o.value === discovery.sentiment) || null
                        }
                        onChange={(selected) =>
                          setDiscoveryField('sentiment', selected?.value ?? null)
                        }
                        isMulti={false}
                        isClearable
                        placeholder="—"
                        menuPortalTarget={document.body}
                      />
                    </div>
                    <div className={styles.discoveryField}>
                      <label>List size</label>
                      <SimpleInput
                        type="number"
                        className={styles.contactInput}
                        value={discovery.list_size}
                        onInput={(value) => setDiscoveryField('list_size', value)}
                      />
                    </div>
                    <div className={styles.discoveryField}>
                      <label>Budget</label>
                      <SimpleInput
                        type="number"
                        className={styles.contactInput}
                        value={discovery.budget}
                        onInput={(value) => setDiscoveryField('budget', value)}
                      />
                    </div>
                    <div className={styles.discoveryField}>
                      <label>Commencement</label>
                      <SimpleInput
                        type="text"
                        placeholder="e.g. 2 weeks, 3 months"
                        className={styles.contactInput}
                        value={discovery.commencement_time}
                        onInput={(value) => setDiscoveryField('commencement_time', value)}
                      />
                    </div>
                  </div>

                  <div className={`${styles.discoveryField} ${styles.discoveryFull}`}>
                    <label>Reason for loss</label>
                    <TextArea
                      rows={2}
                      placeholder="Why was this lead lost?"
                      className={styles.discoveryTextarea}
                      value={discovery.loss_reason}
                      onInput={(value) => setDiscoveryField('loss_reason', value)}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className={styles.discoveryGrid}>
                    {discoveryDisplay.map((item) => (
                      <div key={item.label} className={styles.discoveryField}>
                        <label>{item.label}</label>
                        <span
                          className={item.value ? styles.discoveryValue : styles.discoveryEmpty}
                        >
                          {item.value || '—'}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className={`${styles.discoveryField} ${styles.discoveryFull}`}>
                    <label>Reason for loss</label>
                    <span
                      className={
                        discovery.loss_reason ? styles.discoveryValue : styles.discoveryEmpty
                      }
                    >
                      {discovery.loss_reason || '—'}
                    </span>
                  </div>
                </>
              )}
            </div>
          </section>

          <section
            className={`${styles.section} ${styles.contactPrioritySection} ${styles.contactsCard} ${styles.dossierCol} ${styles.dossierNarrow}`}
          >
            <div className={styles.contactsHeader}>
              <h2 className={styles.sectionTitle}>
                Contacts
                {contacts.length > 0 && (
                  <span className={styles.sectionCount}>{contacts.length}</span>
                )}
              </h2>
              <button
                type="button"
                className={styles.addContactBtn}
                onClick={showContactForm ? handleCancelContactForm : handleAddContactClick}
              >
                {showContactForm ? 'Cancel' : '+ Add contact'}
              </button>
            </div>

            <div className={styles.cardScroll}>
              {showContactForm && (
                <div className={styles.contactForm}>
                  <div className={styles.contactFormRow}>
                    <input
                      className={styles.contactInput}
                      placeholder="First name"
                      value={contactForm.first_name}
                      onChange={(e) => handleContactField('first_name', e.target.value)}
                    />
                    <input
                      className={styles.contactInput}
                      placeholder="Last name"
                      value={contactForm.last_name}
                      onChange={(e) => handleContactField('last_name', e.target.value)}
                    />
                  </div>
                  <input
                    className={styles.contactInput}
                    placeholder="Title"
                    value={contactForm.title}
                    onChange={(e) => handleContactField('title', e.target.value)}
                  />
                  <div className={styles.contactFormRow}>
                    <input
                      className={styles.contactInput}
                      placeholder="Email"
                      value={contactForm.email}
                      onChange={(e) => handleContactField('email', e.target.value)}
                    />
                    <input
                      className={styles.contactInput}
                      placeholder="Phone"
                      value={contactForm.phone}
                      onChange={(e) => handleContactField('phone', e.target.value)}
                    />
                  </div>
                  <input
                    className={styles.contactInput}
                    placeholder="LinkedIn URL"
                    value={contactForm.linkedin_url}
                    onChange={(e) => handleContactField('linkedin_url', e.target.value)}
                  />
                  <div className={styles.contactFormActions}>
                    <button
                      type="button"
                      className={styles.contactSaveBtn}
                      onClick={handleSaveContact}
                      disabled={savingContact || !contactForm.first_name.trim()}
                    >
                      {savingContact
                        ? 'Saving…'
                        : editingContactId
                        ? 'Save changes'
                        : 'Add contact'}
                    </button>
                  </div>
                </div>
              )}

              {contactsLoading && <p className={styles.activityEmpty}>Loading…</p>}
              {!contactsLoading && contacts.length === 0 && (
                <p className={styles.activityEmpty}>No contacts found.</p>
              )}
              {!contactsLoading && contacts.length > 0 && (
                <div className={styles.contactList}>
                  {contactsPreview.map((c, index) => (
                    <ContactCard
                      key={c.id || c._id}
                      contact={c}
                      featured={index === 0}
                      is_key={String(c.id || c._id) === String(keyContactId)}
                      onMakeKey={handleMakeKeyContact}
                      onEdit={handleEditContactClick}
                    />
                  ))}
                </div>
              )}
              {!contactsLoading && contacts.length > contactsPreview.length && (
                <p className={styles.sectionFootnote}>
                  +{contacts.length - contactsPreview.length} more contacts available
                </p>
              )}
            </div>
          </section>
        </div>

        {basicInfo.short_description && (
          <section className={`${styles.section} ${styles.aboutSection}`}>
            <h2 className={styles.sectionTitle}>About</h2>
            <p className={styles.description}>{basicInfo.short_description}</p>
          </section>
        )}

        {lead.keywords && lead.keywords.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Keywords</h2>
            <TagList values={lead.keywords} />
          </section>
        )}
      </div>

      {/* Right pane */}
      <aside className={styles.right}>
        <div className={styles.activityHeader}>
          <div className={styles.activityHeading}>
            <h2 className={styles.sectionTitle}>Activity</h2>
            <span className={styles.activityHint}>Timeline · latest updates first</span>
          </div>
          <ContextMenu
            text={
              <span className={styles.addActivityBtn}>
                <Icon name="add" width={16} height={16} />
              </span>
            }
            callback={(fn) => fn()}
            actions={[
              [
                { label: 'Log activity', action: () => setComposerMode('note') },
                { label: 'Schedule call', action: () => setComposerMode('meeting') },
                { label: 'Send message', action: () => setComposerMode('message') },
                { label: 'Add label', action: () => setComposerMode('label') }
              ]
            ]}
          />
        </div>
        <div className={styles.activitySection}>
          <SubjectTimeline
            subject={timelineSubject}
            host={timelineHost}
            defaultInvitee={timelineInvitee}
            contacts={contacts}
            composer_mode={composerMode}
            onCloseComposer={() => setComposerMode(null)}
          />
        </div>
      </aside>
    </div>
  );
};
